import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Clock, MapPin, Users, ChefHat, ArrowLeft, Check, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [seats, setSeats] = useState(1);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);

  useEffect(() => {
    API.get(`/events/${id}`).then(r => setEvent(r.data)).catch(() => navigate('/events'));
  }, [id, navigate]);

  const handleBook = async () => {
    setBooking(true);
    try {
      await API.post(`/events/${id}/book`, { seats });
      setBooked(true);
      setEvent(prev => ({ ...prev, seats_booked: (prev.seats_booked || 0) + seats }));
      toast.success('Event booked!', { description: `${seats} seat${seats > 1 ? 's' : ''} reserved. Pay via Zelle or Venmo.` });
    } catch (err) {
      toast.error('Booking failed', { description: err.response?.data?.detail || 'Please try again' });
    } finally {
      setBooking(false);
    }
  };

  if (!event) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#E05A47] border-t-transparent rounded-full animate-spin" /></div>;

  const available = event.capacity - (event.seats_booked || 0);

  return (
    <div className="fade-in max-w-4xl mx-auto" data-testid="event-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-[#717171] hover:text-[#222] text-sm font-medium mb-6" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
      </button>

      <img src={event.image || 'https://images.unsplash.com/photo-1768508950719-4d76978fdf44'} alt={event.title} className="w-full h-64 sm:h-80 object-cover rounded-2xl" />

      <div className="mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="inline-block px-3 py-1 rounded-full bg-[#E05A47]/10 text-[#E05A47] text-xs font-semibold mb-3">{event.theme}</div>
            <h1 className="text-3xl font-extrabold text-[#222] font-[Manrope]">{event.title}</h1>
            <p className="mt-3 text-[#717171] leading-relaxed">{event.description}</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-4 bg-[#F7F7F7] rounded-xl">
              <Clock className="w-5 h-5 text-[#E05A47]" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-[#717171]">Date & Time</p>
                <p className="text-sm font-semibold text-[#222]">{new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {event.time}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-[#F7F7F7] rounded-xl">
              <MapPin className="w-5 h-5 text-[#E05A47]" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-[#717171]">Location</p>
                <p className="text-sm font-semibold text-[#222]">{event.location}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-[#F7F7F7] rounded-xl">
              <Users className="w-5 h-5 text-[#E05A47]" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-[#717171]">Availability</p>
                <p className="text-sm font-semibold text-[#222]">{available} seats left</p>
              </div>
            </div>
          </div>

          {event.menu?.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-[#222] font-[Manrope] mb-3 flex items-center gap-2">
                <ChefHat className="w-5 h-5 text-[#E05A47]" strokeWidth={1.5} /> Menu
              </h2>
              <div className="flex flex-wrap gap-2">
                {event.menu.map(item => (
                  <span key={item} className="px-3 py-1.5 bg-[#F7F7F7] rounded-full text-sm text-[#222] font-medium">{item}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Booking Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-[#EBEBEB] p-6 sticky top-24 shadow-sm" data-testid="booking-card">
            <p className="text-2xl font-extrabold text-[#222] font-[Manrope]">${event.price} <span className="text-sm font-normal text-[#717171]">/ seat</span></p>

            {booked ? (
              <div className="mt-6 text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <p className="font-bold text-[#222] font-[Manrope]">Booked!</p>
                <p className="text-sm text-[#717171] mt-1">Pay via Zelle or Venmo</p>
                <Button variant="outline" className="mt-4 w-full rounded-lg" onClick={() => navigate('/bookings')} data-testid="view-bookings-btn">View My Bookings</Button>
              </div>
            ) : (
              <>
                <div className="mt-6 flex items-center justify-between bg-[#F7F7F7] rounded-xl p-3">
                  <span className="text-sm font-medium text-[#222]">Seats</span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setSeats(Math.max(1, seats - 1))} className="w-8 h-8 rounded-full border border-[#EBEBEB] flex items-center justify-center hover:bg-white" data-testid="seats-minus">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-sm font-bold w-6 text-center" data-testid="seats-count">{seats}</span>
                    <button onClick={() => setSeats(Math.min(available, seats + 1))} className="w-8 h-8 rounded-full border border-[#EBEBEB] flex items-center justify-center hover:bg-white" data-testid="seats-plus">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex justify-between text-sm">
                  <span className="text-[#717171]">${event.price} x {seats} seat{seats > 1 ? 's' : ''}</span>
                  <span className="font-bold text-[#222]">${event.price * seats}</span>
                </div>
                {/* error handled via toast */}
                <Button
                  onClick={handleBook}
                  disabled={booking || available === 0}
                  className="w-full mt-4 bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold h-11 btn-press"
                  data-testid="book-event-btn"
                >
                  {booking ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : available === 0 ? 'Sold Out' : 'Reserve Seats'}
                </Button>
                <p className="text-xs text-[#717171] text-center mt-3">Payment via Zelle or Venmo after booking</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
